import {Autocomplete, TextField, TextFieldProps} from '@mui/material';
import isNil from 'lodash/isNil';
import {useCallback, useEffect, useMemo} from 'react';

import {useAppSelector} from '../../hooks/redux';

type Props = Pick<
  TextFieldProps,
  | 'size'
  | 'disabled'
  | 'fullWidth'
  | 'label'
  | 'error'
  | 'helperText'
  | 'InputProps'
  | 'sx'
> & {
  value?: number | null;
  layer?: number | null;
  forceDefault?: boolean;
  nullLabel?: string;
  onChange?: (value?: number | null) => void;
};

export const ZoneSelect = ({
  value,
  layer,
  disabled,
  fullWidth,
  size,
  forceDefault,
  nullLabel,
  onChange,
  ...props
}: Props) => {
  const layers = useAppSelector(({assets}) => assets.layers);
  const zonesSlice = useAppSelector(({assets}) => assets.zones);

  const getLayerName = useCallback(
    (id: number | null) => {
      return layers.find((el) => el.id === id)?.name;
    },
    [layers]
  );

  const zones = useMemo(() => {
    if (layer) {
      return zonesSlice.filter((el) => el.layer_id === layer);
    } else {
      return zonesSlice;
    }
  }, [zonesSlice, layer]);

  const nullValue = -1;

  const options = useMemo(() => {
    if (nullLabel) {
      return [
        {
          id: nullValue,
          name: nullLabel,
          layer_id: null,
        },
        ...zones,
      ];
    }
    return zones;
  }, [zones, nullLabel]);

  const defaultOptionId = useMemo(() => {
    return options[0]?.id;
  }, [options]);

  const selectedOptionId = isNil(value)
    ? forceDefault
      ? defaultOptionId
      : nullValue
    : value;
  const selectedOption = options.find((i) => i.id === selectedOptionId) ?? null;

  useEffect(() => {
    if (isNil(value) && forceDefault && defaultOptionId !== null) {
      onChange?.(defaultOptionId);
    }
  }, [value, defaultOptionId]);

  return disabled ? (
    <TextField
      {...props}
      value={options.find((i) => i.id === value)?.name ?? ''}
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
    />
  ) : (
    <Autocomplete
      value={selectedOption ?? null}
      disabled={disabled}
      fullWidth={fullWidth}
      options={options}
      size={size}
      disableClearable={selectedOptionId === nullValue}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      getOptionLabel={(o) => o.name ?? ''}
      renderInput={(params) => (
        <TextField label="Section" {...props} {...params} />
      )}
      renderOption={(props, option) => {
        return (
          <li {...props} key={option.id}>
            {option.name}{' '}
            {getLayerName(option.layer_id)
              ? `(${getLayerName(option.layer_id)})`
              : ''}
          </li>
        );
      }}
      onChange={(_, option) => {
        const v =
          !option || isNil(option.id) || option.id === nullValue
            ? null
            : option.id;
        onChange?.(v);
      }}
    />
  );
};
